"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import GridRow from "../common/grid-row/grid-row";
import GridColumn from "../common/grid-column/grid-column";
import { Homepage, Page } from "@/sanity/sanity.types";
import imageUrl from "@/sanity/utilities/imageUrl";
import HomeTrust from "./home-trust/home-trust";
import HomeCTAStrip from "./home-cta-strip/home-cta-strip";
import HomeReviews from "./home-reviews/home-reviews";
import Card, { CardStyle } from "../common/card/card";
import HomePoints from "./home-points/home-points";
import { ArticleData } from "@/sanity/interfaces";
import ResourcesArticleBlockAuto from "../resources/resources-article-block/resources-article-block-auto";
import HeroComponent from "./home-hero";
import { usePerformance } from "@/contexts/performance-context";
import { ImageLoading } from "@/utilities/imageLoading";
import { useAs146Decision } from "@/hooks/use-as146-decision";
import { useAs202Decision } from "@/hooks/use-as202-decision";
import AS146HeroComponent from "./as146-hero";
import FinancialHealthBanner, { AS202_BANNER_HREF_BY_VARIANT } from "./financial-health-banner";
interface IHomeProps {
  data: Homepage;
  beforeQueryTime: number;
  afterQueryTime: number;
  latestArticles?: ArticleData[];
}
const Home = props => {
  const $ = _c(54);
  const {
    data,
    beforeQueryTime,
    afterQueryTime,
    latestArticles
  } = props;
  const {
    ctaStrip,
    hero,
    quickOptions,
    homeAnimatedMobileBackground,
    homeAnimatedMobile1,
    homeAnimatedMobile2,
    proof,
    testimonials,
    tools,
    trustBar
  } = data;
  const {
    checkTimeElapsed
  } = usePerformance();
  const [heroHeight, setHeroHeight] = useState(631);
  let t0;
  if ($[0] !== afterQueryTime || $[1] !== beforeQueryTime || $[2] !== checkTimeElapsed) {
    t0 = () => {
      checkTimeElapsed(beforeQueryTime, "Home page before query started");
      checkTimeElapsed(afterQueryTime, "Home page after query completed");
    };
    $[0] = afterQueryTime;
    $[1] = beforeQueryTime;
    $[2] = checkTimeElapsed;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== hero) {
    t2 = () => {
      if (hero?.background?.asset) {
        const img = document.createElement("img");
        img.src = imageUrl(hero.background.asset).url();
        img.onload = () => {
          setHeroHeight(img.height);
        };
      }
    };
    t3 = [hero];
    $[5] = hero;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const {
    shouldRunAs146,
    isDecisionReadyForAs146,
    variationKeyAs146
  } = useAs146Decision();
  const shouldShowAS146 = isDecisionReadyForAs146 && shouldRunAs146;
  const {
    shouldRunAs202,
    isDecisionReadyForAs202,
    variationKeyAs202,
    dma: dmaAs202,
    dmaGroup: dmaGroupAs202
  } = useAs202Decision();
  const shouldShowAS202 = isDecisionReadyForAs202 && shouldRunAs202;
  const t4 = `overflow-hidden transition-all duration-100 ease-out ${!isDecisionReadyForAs146 ? "min-h-[631px] translate-y-0 opacity-100" : "h-0 -translate-y-0 opacity-0"}`;
  let t5;
  if ($[8] !== isDecisionReadyForAs146) {
    t5 = !isDecisionReadyForAs146 && <div className="min-h-[631px]" />;
    $[8] = isDecisionReadyForAs146;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `transition-all duration-100 ease-out ${isDecisionReadyForAs146 ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-0 opacity-0"}`;
  let t8;
  if ($[13] !== hero || $[14] !== heroHeight || $[15] !== shouldShowAS146 || $[16] !== variationKeyAs146) {
    t8 = hero && (shouldShowAS146 && (variationKeyAs146 === "v1" || variationKeyAs146 === "v2") ? <AS146HeroComponent hero={hero} heroHeight={547} variationKeyAs146={variationKeyAs146} As146linkTextName="Get Cash" /> : variationKeyAs146 === "v3" ? <HeroComponent hero={hero} heroHeight={heroHeight} As146linkTextName="Get Cash" /> : <HeroComponent hero={hero} heroHeight={heroHeight} />);
    $[13] = hero;
    $[14] = heroHeight;
    $[15] = shouldShowAS146;
    $[16] = variationKeyAs146;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== trustBar) {
    t10 = trustBar && <HomeTrust data={trustBar} />;
    $[21] = trustBar;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== dmaAs202 || $[24] !== dmaGroupAs202 || $[25] !== quickOptions || $[26] !== shouldShowAS202 || $[27] !== variationKeyAs202) {
    t11 = quickOptions && <GridRow className="!gap-y-6"><GridColumn><h2 className="mb-4 text-center md:mb-0">{quickOptions.heading}</h2></GridColumn>{quickOptions.items?.map(_temp)}{shouldShowAS202 && <GridColumn><FinancialHealthBanner experimentId="AS202" dma={dmaAs202} dmaGroup={dmaGroupAs202} variationKey={variationKeyAs202} href={AS202_BANNER_HREF_BY_VARIANT[variationKeyAs202]} /></GridColumn>}</GridRow>;
    $[23] = dmaAs202;
    $[24] = dmaGroupAs202;
    $[25] = quickOptions;
    $[26] = shouldShowAS202;
    $[27] = variationKeyAs202;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== homeAnimatedMobile1 || $[30] !== homeAnimatedMobile2 || $[31] !== homeAnimatedMobileBackground || $[32] !== proof) {
    t12 = proof && <HomePoints data={proof} homeAnimatedMobileBackground={homeAnimatedMobileBackground} homeAnimatedMobile1={homeAnimatedMobile1} homeAnimatedMobile2={homeAnimatedMobile2} />;
    $[29] = homeAnimatedMobile1;
    $[30] = homeAnimatedMobile2;
    $[31] = homeAnimatedMobileBackground;
    $[32] = proof;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== tools) {
    t13 = !!tools?.tools?.length && <><GridRow className="lg:!mt-[20px]">{tools.heading && <GridColumn><h2 className="text-center">{tools.heading}</h2></GridColumn>}{tools.tools?.map(_temp2)}</GridRow></>;
    $[34] = tools;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== testimonials) {
    t14 = testimonials && <HomeReviews data={testimonials} />;
    $[36] = testimonials;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== latestArticles) {
    t15 = latestArticles && <ResourcesArticleBlockAuto articles={latestArticles} title="Recent Articles" imageLoading={ImageLoading.LAZY} />;
    $[38] = latestArticles;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <div className="flex flex-col gap-12 pb-12 pt-12 md:gap-24 md:pt-24">{t11}{t12}{t13}{t14}{t15}</div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== ctaStrip) {
    t17 = ctaStrip && <HomeCTAStrip data={ctaStrip} />;
    $[46] = ctaStrip;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t10 || $[49] !== t16 || $[50] !== t17 || $[51] !== t6 || $[52] !== t9) {
    t18 = <div className="mx-auto">{t6}{t9}{t10}{t16}{t17}</div>;
    $[48] = t10;
    $[49] = t16;
    $[50] = t17;
    $[51] = t6;
    $[52] = t9;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  return t18;
};
export default Home;
function _temp(item, index) {
  const {
    cta,
    icon,
    summary,
    title
  } = item;
  if (!cta?.link || !icon || !summary || !title) {
    return null;
  }
  return <GridColumn key={index} lg={4}><Card description={summary} imgUrl={icon?.asset ? imageUrl(icon?.asset).width(58).url() : undefined} linkLabel={cta.link.label} linkUrl={cta.link.href ?? "/"} title={title} hoverEffect={true} imgWidth={58} imgHeight={58} ctaId={cta?.link?.ctaId ?? ""} ctaValue={cta?.link?.ctaValue ?? ""} relValue={cta?.link?.nofollow} /></GridColumn>;
}
function _temp2(tool, index_0) {
  const {
    cta: cta_0,
    image,
    summary: summary_0,
    title: title_0
  } = tool;
  if (!cta_0?.link || !image || !summary_0 || !title_0) {
    return null;
  }
  return <GridColumn key={index_0} lg={4}><Card className="!bg-white" description={summary_0} imgUrl={image?.asset ? imageUrl(image.asset).width(327).url() : undefined} linkLabel={cta_0.link.label} linkUrl={cta_0.link.href ?? "/"} title={title_0} imgWidth={327} imgHeight={179} style={CardStyle.Tertiary} ctaId={cta_0?.link?.ctaId ?? ""} ctaValue={cta_0?.link?.ctaValue ?? ""} relValue={cta_0?.link?.nofollow} /></GridColumn>;
}